import React, { useEffect, useState, Fragment } from "react";
import { useEventWizard } from "pages/asset/events/shared/EventWizardContext";
import { ShowHide } from "components/layout";
import { FormCanvas, FormRadio } from "components/form-fields";
import { FleetService } from "services";
import { NotificationInline, NotificationToaster } from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { DateToLocal } from "components/dates";
import { EventAddLaterOptions } from "pages/asset/events/modals/DefaultModels";
import { HelperFunctions } from "helpers";

export function SignoffEvent() {
    
    const { 
        event,
        setEvent,
        eventType,
        loadingData, 
        setLoadingData,
        stepsConfig,
        setStepsConfig,
        saving,
        setSaving,
        setStepIsValid,
        setHasMadeChanges,
        setActiveStep,
        setCanSave
    } = useEventWizard();

    const [stepData, setStepData] = useState({
        signature: "",
        secondSignature: ""
    });

    const [isSignoffValid, setIsSignoffValid] = useState(false);
    const [isSecondSignoffValid, setIsSecondSignoffValid] = useState(false);
    const [signoffNotAllowedReasons, setSignoffNotAllowedReasons] = useState([]);
    const [signoffLater, setSignoffLater] = useState(false);

    const signatureDocumentationTypeId = 1;
    const signoffRestrictedWarningMessage = "You need to be an event signoff contact at the asset's location in order to signoff this event.";
    const unrectifiedSafetyDefectMessage = "All safety-critical defects must be rectified before this event can be signed off.";
    const pmiSpecialProcessingKey = "PMI";

    const config = stepsConfig.SignoffEvent;
    
    useEffect(() => {
        setActiveStep("Signoff");
    }, []);

    function updateSignature(key, value) {
        setStepData({
            ...stepData,
            [key]: value,
        });
    }

    useEffect(() => {
        let reasons = [];

        if (!config.hasBeenSignedOff && config.signoffRestricted) {
            reasons = [];
            reasons.push(signoffRestrictedWarningMessage);
        }
        else if (config.secondSignoffRequired && !config.hasBeenSecondSignedOff) {
            reasons = [];

            if (config.secondSignoffRestricted) {
                reasons.push(signoffRestrictedWarningMessage);
            }
            
            if (config.hasUserAlreadySignedOff) {
                reasons.push("You have already signed off this event.");
            }
        }

        // ER-maintenance PMI events cannot be signed off (first or second signoff) while any safety-critical
        // defect remains unrectified, so the maintenance record sent to ER is never missing a safety fix.
        const isErMaintenancePmiEvent = event?.isErMaintenanceSendingEnabled === true
            && eventType?.specialProcessingKey?.toUpperCase() === pmiSpecialProcessingKey;
        const hasUnrectifiedSafetyDefect = event?.defects?.some(d => d.safety && !d.rectified) === true;

        if (isErMaintenancePmiEvent && hasUnrectifiedSafetyDefect) {
            reasons.push(unrectifiedSafetyDefectMessage);
        }

        if (reasons.length > 0 && !signoffLater) {
            //If there are reasons preventing signoff then they can't signoff so don't give them the option.
            applySignoffLater(true);
        }
        setSignoffNotAllowedReasons(reasons);
        setIsSignoffValid(stepData.signature !== null && stepData.signature !== undefined && stepData.signature !== "");
        setIsSecondSignoffValid(stepData.secondSignature !== null && stepData.secondSignature !== undefined && stepData.secondSignature !== "");
        setStepIsValid(
            ((!config.signoffRequired || config.hasBeenSignedOff) && (!config.secondSignoffRequired || config.hasBeenSecondSignedOff)) ||
            config.signoffLater
        );
    }, [eventType, event, stepData, stepsConfig.SignoffEvent]);

    const handleApiError = (err, message) => {
        const validationError = 400;

        if (err?.response?.status === validationError && err.response.data != null) {
            NotificationToaster.show(
                Intent.WARNING,
                err.response.data
            );
            return;
        } 

        NotificationToaster.show(
            Intent.DANGER,
            message
        );
    };

    function refreshEvent() {
        setLoadingData(true);

        FleetService.getEvent(event.eventId)
            .then((updatedEvent) => {
                setEvent(updatedEvent);
            })
            .finally(() => {
                setLoadingData(false);
            });
    }

    useEffect(() => {
        setCanSave(!saving && (config.hasBeenSignedOff || isSignoffValid) && (!config.showSecondSignoff || isSecondSignoffValid));
    }, [saving, config.hasBeenSignedOff, config.showSecondSignoff, isSignoffValid, isSecondSignoffValid])

    config.onSignOff = (isSecondSignoff) => {
        setSaving(true);

        let signatureFileName = !isSecondSignoff ? "signature.jpeg" : "secondsignature.jpeg";

        return FleetService.addEventDocument(
            event.eventId, 
            HelperFunctions.base64StringToFile(!isSecondSignoff ? stepData.signature : stepData.secondSignature, signatureFileName, true),
            signatureDocumentationTypeId
        )
            .then(() => {
                setHasMadeChanges(true);
                refreshEvent();
                NotificationToaster.show(Intent.SUCCESS, "Event signed off successfully.");
            })
            .catch((err) => handleApiError(err, "Could not signoff, please try again."))
            .finally(() => {
                setSaving(false);
            });
    };

    function getSignoffNotificationContent() {
        return (<Fragment>Event signed off by {event?.signoffUser} on <DateToLocal format='DD/MM/YYYY HH:mm'>{event?.signoffDate}</DateToLocal></Fragment>)
    }

    function getSecondSignoffNotificationContent() {
        return (<Fragment>Event second signed off by {event?.signoffSecondUser} on <DateToLocal format='DD/MM/YYYY HH:mm'>{event?.signoffSecondDate}</DateToLocal></Fragment>);
    }

    function getFailStatusNotificationText() {
        return (
            <Fragment>
                <p>You are not allowed to sign this event off for the following reason(s): </p>
                <ul>
                    {signoffNotAllowedReasons?.map(reason => (<li key={reason}>{reason}</li>))}
                </ul>
            </Fragment>
        );
    }

    function getFormCanvasComponent() {
        let isSecondSignature = config.hasBeenSignedOff;
        return (
            <FormCanvas
                isLoading={loadingData}
                penColour="grey"
                helperText={(
                    <ShowHide 
                        evaluator={eventType?.signoffHelperText !== null && eventType?.signoffHelperText !== undefined}
                        show={<p>{eventType?.signoffHelperText}</p>}
                    />
                )}
                onChange={(value) => updateSignature(isSecondSignature === false ? "signature" : "secondSignature", value.split(",").pop())}
            />
        );
    }

    function onRadioFieldChange(item) {
        applySignoffLater(item.currentTarget.value === "true");
    }

    function applySignoffLater(value) {
        setStepsConfig(prev => ({
            ...prev,
            SignoffEvent: {
                ...prev.SignoffEvent,
                signoffLater: value
            }
        }));

        setSignoffLater(value);
    }

    return (
        <div>
            <h2>Signoff</h2>

            <ShowHide
                evaluator={event?.signoffUser}
                show={(<NotificationInline
                    allowClose={false}
                    text={getSignoffNotificationContent()}
                    intent="success"
                    show={true}
                    icon={null}
                />)}
            />

            <ShowHide
                evaluator={event?.signoffSecondUser}
                show={(<NotificationInline
                    allowClose={false}
                    text={getSecondSignoffNotificationContent()}
                    intent="success"
                    show={true}
                    icon={null}
                />)}
            />

            <ShowHide
                evaluator={signoffNotAllowedReasons.length > 0}
                show={(
                    <NotificationInline
                        allowClose={false}
                        text={getFailStatusNotificationText()}
                        intent="warning"
                        show={true}
                    />
                )}
                hide={(
                    <>
                        <div className="spacer-bottom"></div>
                        <FormRadio
                            disabled={saving}
                            options={EventAddLaterOptions}
                            onChange={(item) => onRadioFieldChange(item)}
                            headingText="When do you want to signoff?"
                            selectedValue={signoffLater}
                            loading={loadingData}
                            id="input-add-signoff-later"
                        />
                    </>
                )}
            />

            <ShowHide
                evaluator={!config.stepCompleted && signoffNotAllowedReasons.length < 1 && !signoffLater}
                show={(getFormCanvasComponent())}
            />
        </div>
    );
}
